import React, { useState, useEffect } from 'react';
import axios from 'axios';
import API_URL from '../config/apiConfig';
import {
    MagnifyingGlassIcon,
    ChatBubbleLeftRightIcon,
    DocumentCheckIcon,
    EnvelopeIcon,
    ChevronDownIcon,
    ChevronUpIcon,
    ArrowDownTrayIcon,
    UserCircleIcon
} from '@heroicons/react/24/outline';

const StatsPage = () => {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [sortField, setSortField] = useState('lastActivity');
    const [sortDirection, setSortDirection] = useState('desc');
    const [expandedUserId, setExpandedUserId] = useState(null);

    useEffect(() => {
        fetchUsersStats();
    }, []);

    const fetchUsersStats = async () => {
        try {
            const token = localStorage.getItem('auth-token');
            const response = await axios.get(`${API_URL}/admin/users-stats`, {
                headers: { 'auth-token': token }
            });
            if (response.data.success) {
                setUsers(response.data.data);
            }
        } catch (error) {
            console.error('Erreur stats utilisateurs:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleSort = (field) => {
        if (sortField === field) {
            setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortDirection('desc');
        }
    };

    const SortIcon = ({ field }) => {
        if (sortField !== field) return <ChevronDownIcon className="w-3 h-3 text-gray-300" />;
        return sortDirection === 'asc'
            ? <ChevronUpIcon className="w-3 h-3 text-[#Ceaa5b]" />
            : <ChevronDownIcon className="w-3 h-3 text-[#Ceaa5b]" />;
    };

    // Filtrage
    const filtered = users.filter(u =>
        (u.name || '').toLowerCase().includes(search.toLowerCase()) ||
        (u.email || '').toLowerCase().includes(search.toLowerCase())
    );

    // Tri
    const sorted = [...filtered].sort((a, b) => {
        let valA = a[sortField];
        let valB = b[sortField];

        if (sortField === 'lastActivity' || sortField === 'createdAt') {
            valA = new Date(valA || 0).getTime();
            valB = new Date(valB || 0).getTime();
        }
        if (typeof valA === 'string') {
            valA = valA.toLowerCase();
            valB = (valB || '').toLowerCase();
        }

        if (valA < valB) return sortDirection === 'asc' ? -1 : 1;
        if (valA > valB) return sortDirection === 'asc' ? 1 : -1;
        return 0;
    });

    // Totaux
    const totals = users.reduce((acc, u) => ({
        conversations: acc.conversations + u.totalConversations,
        reports: acc.reports + u.completedConversations,
        messages: acc.messages + u.totalMessages,
        pdfs: acc.pdfs + u.pdfGenerationCount,
    }), { conversations: 0, reports: 0, messages: 0, pdfs: 0 });

    if (loading) {
        return (
            <div className="flex items-center justify-center py-20">
                <div className="w-8 h-8 border-4 border-[#Ceaa5b] border-t-transparent rounded-full animate-spin"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <h1 className="text-2xl font-bold text-gray-900">Statistiques par utilisateur</h1>

            {/* RÉSUMÉ GLOBAL */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <MiniStat label="Conversations" value={totals.conversations} color="bg-emerald-500" />
                <MiniStat label="Rapports finalisés" value={totals.reports} color="bg-purple-500" />
                <MiniStat label="Messages" value={totals.messages} color="bg-blue-500" />
                <MiniStat label="PDF générés" value={totals.pdfs} color="bg-amber-500" />
            </div>

            {/* BARRE DE RECHERCHE */}
            <div className="relative">
                <MagnifyingGlassIcon className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                    type="text"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Rechercher par nom ou email..."
                    className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#Ceaa5b]/50 focus:border-[#Ceaa5b] text-sm"
                />
            </div>

            {/* TABLEAU */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="bg-gray-50 text-left text-xs text-gray-500 uppercase tracking-wider">
                                <th className="px-4 py-3 font-medium">
                                    <button onClick={() => handleSort('name')} className="flex items-center gap-1 hover:text-gray-900">
                                        Utilisateur <SortIcon field="name" />
                                    </button>
                                </th>
                                <th className="px-4 py-3 font-medium text-center">
                                    <button onClick={() => handleSort('totalConversations')} className="flex items-center gap-1 mx-auto hover:text-gray-900">
                                        Conversations <SortIcon field="totalConversations" />
                                    </button>
                                </th>
                                <th className="px-4 py-3 font-medium text-center">
                                    <button onClick={() => handleSort('completedConversations')} className="flex items-center gap-1 mx-auto hover:text-gray-900">
                                        Rapports <SortIcon field="completedConversations" />
                                    </button>
                                </th>
                                <th className="px-4 py-3 font-medium text-center">
                                    <button onClick={() => handleSort('totalMessages')} className="flex items-center gap-1 mx-auto hover:text-gray-900">
                                        Messages <SortIcon field="totalMessages" />
                                    </button>
                                </th>
                                <th className="px-4 py-3 font-medium text-center">Quota PDF</th>
                                <th className="px-4 py-3 font-medium text-center">
                                    <button onClick={() => handleSort('lastActivity')} className="flex items-center gap-1 mx-auto hover:text-gray-900">
                                        Dernière activité <SortIcon field="lastActivity" />
                                    </button>
                                </th>
                                <th className="px-4 py-3 font-medium text-center">Statut</th>
                                <th className="px-4 py-3 font-medium w-10"></th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-50">
                            {sorted.length === 0 ? (
                                <tr>
                                    <td colSpan={8} className="px-4 py-10 text-center text-gray-400 italic">
                                        Aucun utilisateur trouvé.
                                    </td>
                                </tr>
                            ) : (
                                sorted.map(user => (
                                    <UserRow
                                        key={user.id}
                                        user={user}
                                        isExpanded={expandedUserId === user.id}
                                        onToggle={() => setExpandedUserId(expandedUserId === user.id ? null : user.id)}
                                    />
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            <p className="text-xs text-gray-400 text-right">{sorted.length} utilisateur{sorted.length > 1 ? 's' : ''} affichés</p>
        </div>
    );
};

/* =================== LIGNE UTILISATEUR =================== */
const UserRow = ({ user, isExpanded, onToggle }) => {
    const usage = user.pdfMonthlyQuota > 0
        ? Math.round(((user.pdfMonthlyCount || 0) / user.pdfMonthlyQuota) * 100)
        : 0;
    const barColor = usage > 80 ? 'bg-red-500' : usage > 50 ? 'bg-amber-500' : 'bg-emerald-500';

    const timeAgo = (date) => {
        if (!date) return 'Jamais';
        const diff = Date.now() - new Date(date).getTime();
        const minutes = Math.floor(diff / 60000);
        if (minutes < 1) return "A l'instant";
        if (minutes < 60) return `Il y a ${minutes}min`;
        const hours = Math.floor(minutes / 60);
        if (hours < 24) return `Il y a ${hours}h`;
        const days = Math.floor(hours / 24);
        if (days < 7) return `Il y a ${days}j`;
        return new Date(date).toLocaleDateString('fr-FR');
    };

    return (
        <>
            <tr
                className={`hover:bg-gray-50/50 cursor-pointer transition-colors ${isExpanded ? 'bg-blue-50/30' : ''}`}
                onClick={onToggle}
            >
                {/* UTILISATEUR */}
                <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                        <div className={`w-9 h-9 rounded-full flex items-center justify-center text-white text-sm font-bold flex-shrink-0 ${user.isActive ? 'bg-[#1c2143]' : 'bg-gray-400'}`}>
                            {(user.name || 'U').charAt(0).toUpperCase()}
                        </div>
                        <div className="min-w-0">
                            <p className="font-medium text-gray-900 truncate">{user.name || 'Sans nom'}</p>
                            <p className="text-xs text-gray-400 truncate">{user.email}</p>
                        </div>
                    </div>
                </td>

                {/* CONVERSATIONS */}
                <td className="px-4 py-3 text-center">
                    <div className="flex items-center justify-center gap-1.5">
                        <ChatBubbleLeftRightIcon className="w-4 h-4 text-emerald-500" />
                        <span className="font-semibold text-gray-900">{user.totalConversations}</span>
                        {user.activeConversations > 0 && (
                            <span className="text-[10px] text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded-full">{user.activeConversations} en cours</span>
                        )}
                    </div>
                </td>

                {/* RAPPORTS */}
                <td className="px-4 py-3 text-center">
                    <div className="flex items-center justify-center gap-1.5">
                        <DocumentCheckIcon className="w-4 h-4 text-purple-500" />
                        <span className="font-semibold text-gray-900">{user.completedConversations}</span>
                    </div>
                </td>

                {/* MESSAGES */}
                <td className="px-4 py-3 text-center font-medium text-gray-700">
                    {user.totalMessages}
                </td>

                {/* QUOTA */}
                <td className="px-4 py-3">
                    <div className="flex flex-col items-center gap-1">
                        <span className="text-xs text-gray-500">{user.pdfMonthlyCount || 0} / {user.pdfMonthlyQuota}</span>
                        <div className="w-20 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                            <div className={`h-full rounded-full ${barColor}`} style={{ width: `${Math.min(usage, 100)}%` }}></div>
                        </div>
                    </div>
                </td>

                {/* DERNIÈRE ACTIVITÉ */}
                <td className="px-4 py-3 text-center text-xs text-gray-500">
                    {timeAgo(user.lastActivity)}
                </td>

                {/* STATUT */}
                <td className="px-4 py-3 text-center">
                    <span className={`text-[11px] px-2 py-1 rounded-full font-medium ${user.isActive ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
                        {user.isActive ? 'Actif' : 'Inactif'}
                    </span>
                </td>

                {/* CHEVRON */}
                <td className="px-4 py-3 text-center">
                    {isExpanded
                        ? <ChevronUpIcon className="w-4 h-4 text-[#Ceaa5b] mx-auto" />
                        : <ChevronDownIcon className="w-4 h-4 text-gray-400 mx-auto" />
                    }
                </td>
            </tr>

            {/* LIGNE DÉTAIL DÉPLIÉ */}
            {isExpanded && (
                <tr>
                    <td colSpan={8} className="px-4 py-0">
                        <div className="py-4 pl-12 pr-4 bg-gray-50/50 border-l-4 border-[#Ceaa5b] rounded-b-lg mb-1 space-y-4">
                            {/* INFOS */}
                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-xs">
                                <div>
                                    <span className="text-gray-400 block">Inscrit le</span>
                                    <span className="font-medium text-gray-900">
                                        {new Date(user.createdAt).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}
                                    </span>
                                </div>
                                <div>
                                    <span className="text-gray-400 block">Type</span>
                                    <div className="flex items-center gap-1 mt-0.5">
                                        {user.isAdmin && <span className="px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded text-[10px] font-medium">Admin</span>}
                                        {user.isDemo && <span className="px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded text-[10px] font-medium">Demo</span>}
                                        {!user.isAdmin && !user.isDemo && <span className="px-1.5 py-0.5 bg-gray-100 text-gray-600 rounded text-[10px] font-medium">Client</span>}
                                    </div>
                                </div>
                                <div>
                                    <span className="text-gray-400 block">Email</span>
                                    <a href={`mailto:${user.email}`} className="font-medium text-[#Ceaa5b] hover:underline flex items-center gap-1">
                                        <EnvelopeIcon className="w-3 h-3" />
                                        {user.email}
                                    </a>
                                </div>
                                <div>
                                    <span className="text-gray-400 block">Moy. messages/conv.</span>
                                    <span className="font-medium text-gray-900">
                                        {user.totalConversations > 0 ? Math.round(user.totalMessages / user.totalConversations) : 0}
                                    </span>
                                </div>
                            </div>

                            {/* RAPPORTS */}
                            {user.reports && user.reports.length > 0 && (
                                <div>
                                    <h4 className="text-xs text-gray-400 uppercase tracking-wider font-medium mb-2">Derniers rapports</h4>
                                    <div className="space-y-1.5">
                                        {user.reports.map(report => (
                                            <div key={report.id} className="flex items-center justify-between bg-white rounded-lg px-3 py-2 border border-gray-100">
                                                <div className="flex items-center gap-2">
                                                    <DocumentCheckIcon className="w-4 h-4 text-[#Ceaa5b]" />
                                                    <span className="text-sm font-medium text-gray-900 truncate max-w-[250px]">
                                                        {report.title || 'Rapport'}
                                                    </span>
                                                </div>
                                                <div className="flex items-center gap-3">
                                                    <span className="text-[10px] text-gray-400">
                                                        {new Date(report.updatedAt).toLocaleDateString('fr-FR')}
                                                    </span>
                                                    {report.pdfUrl && (
                                                        <a
                                                            href={report.pdfUrl}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="flex items-center gap-1 text-xs text-[#Ceaa5b] hover:text-[#1c2143] font-medium transition-colors"
                                                            onClick={(e) => e.stopPropagation()}
                                                        >
                                                            <ArrowDownTrayIcon className="w-3.5 h-3.5" />
                                                            PDF
                                                        </a>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {user.reports && user.reports.length === 0 && user.completedConversations === 0 && (
                                <p className="text-xs text-gray-400 italic">Aucun rapport finalisé pour cet utilisateur.</p>
                            )}
                        </div>
                    </td>
                </tr>
            )}
        </>
    );
};

/* =================== MINI STAT =================== */
const MiniStat = ({ label, value, color }) => (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 flex items-center gap-3">
        <div className={`w-2 h-10 rounded-full ${color}`}></div>
        <div>
            <p className="text-2xl font-bold text-gray-900">{value}</p>
            <p className="text-xs text-gray-500">{label}</p>
        </div>
    </div>
);

export default StatsPage;
